import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Tooltip,
  Typography,
  Badge,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ForumIcon from '@mui/icons-material/Forum';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearIcon from '@mui/icons-material/Clear';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  getLiveFolders,
  getLiveThreads,
  getLiveUids,
  getThreads,
  liveCopyMessage,
  liveEmptyTrash,
  liveMoveMessage,
  liveTrashMessage,
  LiveThreadSummary,
  ThreadSummary,
} from '../api/mailbox';
import MessageContextMenu, {
  MessageActionTarget,
  setMessageDragData,
} from './MessageContextMenu';
import FolderTransferDialog from './FolderTransferDialog';
import { liveThreadPath } from '../utils/imapPaths';

interface ThreadListProps {
  accountId?: number;
  imapFolder?: string;
  title: string;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

type SortBy = 'date' | 'from' | 'size';
type SortOrder = 'asc' | 'desc';

type SelectionEntry = {
  account: number;
  folder: string;
  uids: number[];
};

function threadSelectionKey(thread: LiveThreadSummary): string {
  return `${thread.account}:${thread.imap_folder}:${thread.thread_id || thread.latest_uid}`;
}

function threadUids(thread: LiveThreadSummary): number[] {
  if (thread.message_uids?.length) return thread.message_uids;
  return thread.latest_uid != null ? [thread.latest_uid] : [];
}

function formatSize(bytes?: number): string {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ThreadList({ accountId, imapFolder, title }: ThreadListProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const [menuMessage, setMenuMessage] = useState<MessageActionTarget | null>(null);
  const [transferMode, setTransferMode] = useState<'move' | 'copy' | null>(null);
  const [singleMoveTarget, setSingleMoveTarget] = useState<MessageActionTarget | null>(null);
  const [selected, setSelected] = useState<Record<string, SelectionEntry>>({});
  const [folderSelectMode, setFolderSelectMode] = useState(false);
  const [folderUids, setFolderUids] = useState<number[]>([]);
  const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortBy>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  // Clear selection when changing folder / search / sort
  useEffect(() => {
    setSelected({});
    setFolderSelectMode(false);
    setFolderUids([]);
    setActionError(null);
  }, [accountId, imapFolder, search]);

  useEffect(() => {
    setPage(1);
  }, [sortBy, sortOrder]);

  const searching = Boolean(search);
  const liveMode = !searching && Boolean(accountId && imapFolder);

  const foldersQuery = useQuery({
    queryKey: ['live-folders'],
    queryFn: () => getLiveFolders(),
    enabled: liveMode,
  });

  const currentFolderMeta = useMemo(() => {
    if (!liveMode || !accountId || !imapFolder) return null;
    return (foldersQuery.data?.results || []).find(
      (f) => f.account === accountId && f.imap_name === imapFolder
    );
  }, [foldersQuery.data, liveMode, accountId, imapFolder]);

  const isTrashFolder = currentFolderMeta?.special_use === 'trash';

  const liveQuery = useQuery({
    queryKey: ['live-threads', accountId, imapFolder, page, sortBy, sortOrder],
    queryFn: () =>
      getLiveThreads({
        account: accountId!,
        folder: imapFolder!,
        page,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        order: sortOrder,
      }),
    enabled: liveMode,
    placeholderData: (prev) => prev,
  });

  const searchQuery = useQuery({
    queryKey: ['threads', 'all', page, search],
    queryFn: () => getThreads({ page, page_size: PAGE_SIZE, search }),
    enabled: searching,
    placeholderData: (prev) => prev,
  });

  const data = searching ? searchQuery.data : liveQuery.data;
  const isLoading = searching ? searchQuery.isLoading : liveQuery.isLoading;
  const isError = searching ? searchQuery.isError : liveQuery.isError;
  const error = searching ? searchQuery.error : liveQuery.error;
  const isFetching = searching ? searchQuery.isFetching : liveQuery.isFetching;

  const threads = data?.results || [];
  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  const liveThreads = liveMode ? (threads as LiveThreadSummary[]) : [];

  const pageKeys = useMemo(
    () => liveThreads.map(threadSelectionKey),
    [liveThreads]
  );

  const selectedCount = folderSelectMode
    ? folderUids.length
    : Object.keys(selected).length;

  const selectedUidCount = folderSelectMode
    ? folderUids.length
    : Object.values(selected).reduce((n, e) => n + e.uids.length, 0);

  const allPageSelected =
    liveMode &&
    pageKeys.length > 0 &&
    !folderSelectMode &&
    pageKeys.every((k) => Boolean(selected[k]));

  const somePageSelected =
    liveMode && !folderSelectMode && pageKeys.some((k) => Boolean(selected[k]));

  const formatDate = (dateStr: string) => {
    try {
      const d = parseISO(dateStr);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) return format(d, 'HH:mm');
      return format(d, 'MMM d');
    } catch {
      return '';
    }
  };

  const toActionTarget = (thread: ThreadSummary | LiveThreadSummary): MessageActionTarget | null => {
    const live = thread as LiveThreadSummary;
    if (live.account && live.imap_folder && live.latest_uid != null) {
      return {
        account: live.account,
        imap_folder: live.imap_folder,
        uid: live.latest_uid,
        folder: live.folder_id,
        from_address: live.latest_from,
        subject: live.subject,
      };
    }
    if (!thread.latest_message_id) return null;
    return {
      id: thread.latest_message_id,
      folder: thread.folder_id,
      from_address: thread.latest_from,
      subject: thread.subject,
    };
  };

  const openThread = (thread: ThreadSummary | LiveThreadSummary) => {
    const live = thread as LiveThreadSummary;
    if (live.account && live.imap_folder && live.thread_id) {
      navigate(
        liveThreadPath(live.account, live.imap_folder, live.thread_id, live.message_uids)
      );
      return;
    }
    navigate(`/thread/${thread.id}`);
  };

  const toggleThread = (thread: LiveThreadSummary) => {
    const key = threadSelectionKey(thread);
    const uids = threadUids(thread);
    if (!uids.length) return;
    setFolderSelectMode(false);
    setFolderUids([]);
    setSelected((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: {
          account: thread.account,
          folder: thread.imap_folder,
          uids,
        },
      };
    });
  };

  const selectAllOnPage = () => {
    if (!liveMode) return;
    setFolderSelectMode(false);
    setFolderUids([]);
    setSelected((prev) => {
      const next = { ...prev };
      liveThreads.forEach((thread) => {
        const uids = threadUids(thread);
        if (!uids.length) return;
        next[threadSelectionKey(thread)] = {
          account: thread.account,
          folder: thread.imap_folder,
          uids,
        };
      });
      return next;
    });
  };

  const clearSelection = () => {
    setSelected({});
    setFolderSelectMode(false);
    setFolderUids([]);
  };

  const selectAllInFolder = async () => {
    if (!accountId || !imapFolder) return;
    setActionError(null);
    try {
      const res = await getLiveUids({ account: accountId, folder: imapFolder });
      setFolderSelectMode(true);
      setFolderUids(res.uids);
      setSelected({});
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not select all messages in this folder.';
      setActionError(String(detail));
    }
  };

  const collectSelectedUids = (): { account: number; folder: string; uids: number[] } | null => {
    if (!accountId || !imapFolder) return null;
    if (folderSelectMode) {
      return { account: accountId, folder: imapFolder, uids: folderUids };
    }
    const entries = Object.values(selected);
    if (!entries.length) return null;
    // Selection is always within the current folder in live mode
    const uids = Array.from(new Set(entries.flatMap((e) => e.uids)));
    return { account: accountId, folder: imapFolder, uids };
  };

  const invalidateLists = () => {
    qc.invalidateQueries({ queryKey: ['live-threads'] });
    qc.invalidateQueries({ queryKey: ['threads'] });
    qc.invalidateQueries({ queryKey: ['live-folders'] });
  };

  const bulkMutation = useMutation({
    mutationFn: async (opts: {
      action: 'move' | 'copy' | 'trash';
      dest_folder?: string;
      dest_account?: number;
    }) => {
      const batch = collectSelectedUids();
      if (!batch?.uids.length) throw new Error('Nothing selected.');
      if (opts.action === 'move') {
        await liveMoveMessage({
          account: batch.account,
          folder: batch.folder,
          uids: batch.uids,
          dest_folder: opts.dest_folder!,
          dest_account: opts.dest_account,
        });
      } else if (opts.action === 'copy') {
        await liveCopyMessage({
          account: batch.account,
          folder: batch.folder,
          uids: batch.uids,
          dest_folder: opts.dest_folder!,
          dest_account: opts.dest_account,
        });
      } else {
        await liveTrashMessage({
          account: batch.account,
          folder: batch.folder,
          uids: batch.uids,
        });
      }
    },
    onSuccess: () => {
      clearSelection();
      setTransferMode(null);
      setActionError(null);
      invalidateLists();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err as Error)?.message ||
        'Action failed.';
      setActionError(String(detail));
    },
  });

  const emptyTrashMutation = useMutation({
    mutationFn: async () => {
      if (!accountId) throw new Error('No account.');
      return liveEmptyTrash({ account: accountId });
    },
    onSuccess: () => {
      setConfirmEmptyTrash(false);
      clearSelection();
      invalidateLists();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not empty trash.';
      setActionError(String(detail));
      setConfirmEmptyTrash(false);
    },
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
            {searching ? 'Search results' : title}
          </Typography>
          {liveMode && isTrashFolder && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<DeleteSweepIcon />}
              onClick={() => setConfirmEmptyTrash(true)}
              disabled={emptyTrashMutation.isPending || (data?.count || 0) === 0}
            >
              Empty trash
            </Button>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {searching
            ? `All mail matching “${search}”`
            : 'Live IMAP — select, right-click, or drag to a folder'}
        </Typography>
        <TextField
          size="small"
          fullWidth
          placeholder="Search indexed mail (from, subject, body)…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ mt: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : undefined,
          }}
          inputProps={{ 'aria-label': 'Search all mail' }}
        />
        {liveMode && (
          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="sort-by-label">Sort by</InputLabel>
              <Select
                labelId="sort-by-label"
                label="Sort by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
              >
                <MenuItem value="date">Date</MenuItem>
                <MenuItem value="from">Sender</MenuItem>
                <MenuItem value="size">Size</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={sortOrder === 'desc' ? 'Newest / Z–A / largest first' : 'Oldest / A–Z / smallest first'}>
              <IconButton
                size="small"
                onClick={() => setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))}
                aria-label="Toggle sort order"
              >
                {sortOrder === 'desc' ? (
                  <ArrowDownwardIcon fontSize="small" />
                ) : (
                  <ArrowUpwardIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Typography variant="caption" color="text.secondary">
              {sortOrder === 'desc' ? 'Descending' : 'Ascending'}
            </Typography>
          </Box>
        )}
      </Box>

      {liveMode && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            bgcolor: selectedCount ? 'action.selected' : 'transparent',
          }}
        >
          <Checkbox
            size="small"
            checked={allPageSelected || (folderSelectMode && folderUids.length > 0)}
            indeterminate={somePageSelected && !allPageSelected}
            onChange={(_, checked) => {
              if (checked) selectAllOnPage();
              else clearSelection();
            }}
            disabled={!liveThreads.length && !folderSelectMode}
            inputProps={{ 'aria-label': 'Select all on this page' }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            {selectedCount
              ? folderSelectMode
                ? `${selectedUidCount} selected (entire folder)`
                : `${selectedCount} conversation${selectedCount === 1 ? '' : 's'} (${selectedUidCount} msg)`
              : 'Select'}
          </Typography>
          <Tooltip title="Select all on this page">
            <span>
              <IconButton
                size="small"
                onClick={selectAllOnPage}
                disabled={!liveThreads.length}
                aria-label="Select all on page"
              >
                <SelectAllIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Button size="small" onClick={selectAllInFolder} disabled={bulkMutation.isPending}>
            Select all in folder
          </Button>
          {selectedCount > 0 && (
            <>
              <Button
                size="small"
                startIcon={<DriveFileMoveIcon />}
                onClick={() => setTransferMode('move')}
                disabled={bulkMutation.isPending}
              >
                Move
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={() => setTransferMode('copy')}
                disabled={bulkMutation.isPending}
              >
                Copy
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => bulkMutation.mutate({ action: 'trash' })}
                disabled={bulkMutation.isPending}
              >
                {isTrashFolder ? 'Delete forever' : 'Delete'}
              </Button>
              <IconButton size="small" onClick={clearSelection} aria-label="Clear selection">
                <ClearIcon fontSize="small" />
              </IconButton>
            </>
          )}
          {bulkMutation.isPending && <CircularProgress size={16} sx={{ ml: 1 }} />}
        </Box>
      )}

      {actionError && (
        <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      {!liveMode && !searching ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Select a folder.</Typography>
        </Box>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">
            {(error as any)?.response?.data?.detail || 'Could not load conversations.'}
          </Typography>
        </Box>
      ) : (
        <>
          <List disablePadding sx={{ flexGrow: 1, overflow: 'auto' }}>
            {threads.length === 0 && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {searching ? `No conversations match “${search}”.` : 'No conversations.'}
                </Typography>
              </Box>
            )}
            {threads.map((thread: ThreadSummary | LiveThreadSummary) => {
              const target = toActionTarget(thread);
              const live = thread as LiveThreadSummary;
              const key =
                liveMode && live.account
                  ? threadSelectionKey(live)
                  : `db-${thread.id}-${thread.latest_message_id}`;
              const isSelected =
                liveMode &&
                (folderSelectMode || Boolean(selected[key]));

              return (
                <React.Fragment key={key}>
                  <ListItemButton
                    alignItems="flex-start"
                    draggable={Boolean(target) && !selectedCount}
                    onDragStart={(e) => {
                      if (target) setMessageDragData(e, target);
                    }}
                    onContextMenu={(e) => {
                      if (!target) return;
                      e.preventDefault();
                      setMenuMessage(target);
                      setMenuPos({ top: e.clientY, left: e.clientX });
                    }}
                    onClick={() => openThread(thread)}
                    selected={isSelected}
                    sx={{
                      bgcolor: thread.has_unread ? 'action.hover' : 'inherit',
                      cursor: 'grab',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    {liveMode && (
                      <Checkbox
                        size="small"
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleThread(live)}
                        inputProps={{ 'aria-label': 'Select conversation' }}
                        sx={{ mt: 0.25 }}
                      />
                    )}
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <Badge
                        badgeContent={thread.message_count > 1 ? thread.message_count : 0}
                        color="primary"
                        max={99}
                      >
                        <ForumIcon color={thread.has_unread ? 'primary' : 'action'} />
                      </Badge>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={thread.has_unread ? 700 : 400}
                            noWrap
                            sx={{ flexGrow: 1 }}
                          >
                            {thread.latest_from || thread.participants?.[0] || '(unknown)'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {thread.last_message_date ? formatDate(thread.last_message_date) : ''}
                          </Typography>
                          {thread.has_attachments && (
                            <Tooltip title="Has attachment">
                              <AttachFileIcon
                                fontSize="small"
                                color="action"
                                sx={{ ml: 0.25, flexShrink: 0 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            noWrap
                            fontWeight={thread.has_unread ? 600 : 400}
                          >
                            {thread.subject || '(no subject)'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                            {thread.message_count > 1 && (
                              <Chip
                                size="small"
                                label={`${thread.message_count} messages`}
                                sx={{ height: 20 }}
                              />
                            )}
                            {typeof thread.size === 'number' && thread.size > 0 && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={formatSize(thread.size)}
                                sx={{ height: 20 }}
                              />
                            )}
                            {searching && thread.folder_name && (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={thread.folder_name}
                                sx={{ height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    {target && !selectedCount && (
                      <Tooltip title="Move">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSingleMoveTarget(target);
                          }}
                          aria-label="Move conversation"
                        >
                          <DriveFileMoveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ListItemButton>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderTop: 1, borderColor: 'divider' }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, v) => setPage(v)}
                size="small"
              />
            </Box>
          )}
        </>
      )}

      <MessageContextMenu
        anchorPosition={menuPos}
        message={menuMessage}
        onClose={() => {
          setMenuPos(null);
          setMenuMessage(null);
        }}
      />

      <FolderTransferDialog
        open={Boolean(transferMode) || Boolean(singleMoveTarget)}
        mode={transferMode || 'move'}
        excludeFolderId={singleMoveTarget?.folder}
        excludeImapFolder={imapFolder}
        onClose={() => {
          setTransferMode(null);
          setSingleMoveTarget(null);
        }}
        onSelectLive={async (dest) => {
          if (transferMode) {
            await bulkMutation.mutateAsync({
              action: transferMode,
              dest_folder: dest.imap_name,
              dest_account: dest.account,
            });
            return;
          }
          if (!singleMoveTarget) return;
          if (
            singleMoveTarget.account &&
            singleMoveTarget.imap_folder &&
            singleMoveTarget.uid != null
          ) {
            await liveMoveMessage({
              account: singleMoveTarget.account,
              folder: singleMoveTarget.imap_folder,
              uid: singleMoveTarget.uid,
              dest_folder: dest.imap_name,
              dest_account: dest.account,
            });
          }
          invalidateLists();
        }}
      />

      <Dialog open={confirmEmptyTrash} onClose={() => setConfirmEmptyTrash(false)}>
        <DialogTitle>Empty trash?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Permanently delete all messages in Trash? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEmptyTrash(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={emptyTrashMutation.isPending}
            onClick={() => emptyTrashMutation.mutate()}
          >
            Empty trash
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
